import { faChevronLeft } from "@jewlr/pro-light-svg-icons/faChevronLeft"
import { faTimes } from "@jewlr/pro-light-svg-icons/faTimes"
import { FocusScope } from "@react-aria/focus"
import { useKeyboard } from "@react-aria/interactions"
import { motion, AnimatePresence } from "framer-motion"
import PropTypes from "prop-types"
import { useCallback, useEffect, useRef } from "react"
import { Portal } from "react-portal"
import { RemoveScroll } from "react-remove-scroll"

import Button from "components/ui/button"
import FontAwesomeIcon from "components/ui/font-awesome-icon"
import useAriaHidden from "helpers/use-aria-hidden"
import useHandleOutsideClick from "helpers/use-handle-outside-click"
import useWindowDimensions from "helpers/window-dimensions"
import { cn } from "styles/utils"

const underlayVariants = {
  hidden: { opacity: 0, transition: { delay: ".2" } },
  visible: { opacity: 1 },
}
const modalSlide = {
  hidden: {
    transition: { duration: ".1", ease: "easeOut" },
    translateY: "100vh",
  },
  leave: {
    transition: { duration: ".1", ease: "easeOut" },
    translateY: "100vh",
  },
  visible: {
    transition: { delay: ".2", duration: ".1", ease: "easeOut" },
    translateY: "0vh",
  },
}
const modalSlideX = {
  hidden: {
    transition: { duration: ".1", ease: "easeOut" },
    translateX: "100vw",
  },
  leave: {
    transition: { duration: ".1", ease: "easeOut" },
    translateX: "100vw",
  },
  visible: {
    transition: { delay: ".2", duration: ".1", ease: "easeOut" },
    translateX: "0vw",
  },
}
const modalPop = {
  hidden: {
    opacity: 0,
    scale: 0.25,
    transition: { duration: ".1", ease: "easeOut" },
  },
  leave: {
    opacity: 0,
    scale: 0,
    transition: { duration: ".1", ease: "easeOut" },
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { delay: ".2", duration: ".1", ease: "easeOut" },
  },
}

const getMotionVariants = (modalType) => {
  switch (modalType) {
    case "popup":
      return modalPop
    case "slideright":
      return modalSlideX
    default:
      return modalSlide
  }
}

const getModalHeight = (height) => {
  switch (height) {
    case "fit-content":
      return "auto"
    case "full":
      return "100%"
    default:
      return height || "100%"
  }
}

export const BackButton = ({ handleClick }) => (
  <Button
    aria-label="Back"
    className="absolute left-16 top-16 z-40"
    onClick={handleClick}
  >
    <FontAwesomeIcon className="text-grey-65 size-20" icon={faChevronLeft} />
  </Button>
)

BackButton.propTypes = {
  handleClick: PropTypes.func,
}

export const CloseButton = ({ handleClose }) => (
  <Button
    aria-label="Close"
    className="absolute right-16 top-16 z-40"
    data-cy="half-modal-close-button"
    onClick={handleClose}
  >
    <FontAwesomeIcon className="text-grey-65 size-24" icon={faTimes} />
  </Button>
)

CloseButton.propTypes = {
  handleClose: PropTypes.func,
}

const Modal = ({
  ariaLabel,
  ariaLabelledBy,
  children,
  contentClassName,
  contentIndex,
  disableClose,
  headerClassName,
  headerHTML,
  headerVariant,
  height,
  label,
  labelClassName,
  modalClassName,
  modalType,
  noHeader,
  onBack,
  onClose,
  onOutsideClick,
  shards,
  show,
  variant,
  width,
  withBackButton,
}) => {
  const dialogRef = useRef()
  const contentRef = useRef()
  const { isMobile } = useWindowDimensions()

  const modalHeight = isMobile ? getModalHeight(height) : "auto"
  const motionVariants = getMotionVariants(modalType)

  const handleKeyDown = useCallback(
    (e) => {
      {
        if (e.key === "Escape") {
          if (e.defaultPrevented) return

          if (typeof onClose === "function") {
            onClose()
            e.preventDefault()
          }
        }
      }
    },
    [onClose]
  )
  const { keyboardProps } = useKeyboard({ onKeyDown: handleKeyDown })

  const handleOutsideClick = useCallback(
    (e) => {
      if (typeof onOutsideClick === "function") {
        onOutsideClick(e)
      } else if (typeof onClose === "function") {
        onClose(e)
      }
    },
    [onOutsideClick, onClose]
  )

  useAriaHidden({ enabled: show, ref: dialogRef, shards })
  useHandleOutsideClick({
    enabled: show,
    onOutsideClick: handleOutsideClick,
    ref: dialogRef,
    shards,
  })

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0
    }
  }, [contentIndex])

  useEffect(() => {
    if (document.body) {
      if (show) {
        document.body.classList.add("with-modal")
      } else {
        document.body.classList.remove("with-modal")
      }
    }
  }, [show])

  if (
    sl.config.env !== "production" &&
    noHeader &&
    !(ariaLabel || ariaLabelledBy)
  ) {
    console.warn(
      "Modal must have an accessible name (`aria-label` or `aria-labelledby`)"
    )
  }

  return (
    <AnimatePresence>
      {show && (
        <Portal node={document?.body}>
          <div className="fixed inset-0 z-40">
            <motion.div
              animate="visible"
              className="fixed inset-0 -z-10 flex items-center bg-[rgba(0,0,0,0.5)]"
              exit="hidden"
              initial="hidden"
              variants={underlayVariants}
            />
            <RemoveScroll forwardProps>
              <div
                className={cn(
                  "tablet:items-center flex h-full justify-center",
                  modalType === "popup" ? "items-center" : "items-end"
                )}
              >
                <motion.div
                  animate="visible"
                  {...(noHeader || ariaLabel || ariaLabelledBy
                    ? {
                        "aria-label": ariaLabel,
                        "aria-labelledby": ariaLabelledBy,
                      }
                    : {
                        "aria-labelledby": "modal_header",
                      })}
                  aria-modal
                  className={cn(
                    "min-w-320 tablet:max-h-[90vh] tablet:max-w-[95vw] tablet:w-auto flex max-h-full w-full flex-col overflow-hidden bg-white transition-all duration-200",
                    modalType === "popup" && "max-h-[90vh] w-[90vw] rounded-lg",
                    variant === "squircle" && "rounded-2xl",
                    variant === "squircle" &&
                      modalType === "slideup" &&
                      "tablet:rounded-2xl rounded-b-none rounded-t-3xl",
                    modalClassName
                  )}
                  data-cy="modal"
                  exit="leave"
                  initial="hidden"
                  ref={dialogRef}
                  role="dialog"
                  style={{ height: modalHeight }}
                  variants={motionVariants}
                  {...keyboardProps}
                >
                  <FocusScope autoFocus contain restoreFocus>
                    {!noHeader && (
                      <div
                        className={cn(
                          headerHTML || noHeader
                            ? "relative z-40 shrink-0 grow-0"
                            : [
                                "relative z-40 flex flex-auto shrink-0 grow-0 px-24",
                                headerVariant === "inline"
                                  ? "flex-start"
                                  : "justify-center text-center",
                                headerVariant !== "float" &&
                                  `min-h-40 pt-24 ${
                                    variant === "squircle" ? "pb-10" : "pb-16"
                                  }`,
                              ],
                          headerClassName
                        )}
                      >
                        {headerHTML ?? (
                          <>
                            {withBackButton && contentIndex !== 0 && (
                              <BackButton handleClick={onBack} />
                            )}
                            {!disableClose && (
                              <CloseButton handleClose={onClose} />
                            )}
                            {label && (
                              <h2
                                className={cn(
                                  "font-bold leading-none text-black",
                                  headerVariant === "inline"
                                    ? "text-20"
                                    : "text-24 tablet:text-28",
                                  labelClassName
                                )}
                                id="modal_header"
                              >
                                {label}
                              </h2>
                            )}
                          </>
                        )}
                      </div>
                    )}
                    <div
                      className={cn(
                        "relative w-full flex-1 overflow-y-auto px-24",
                        variant === "squircle" ? "pb-24" : "tablet:pb-32 pb-56",
                        contentClassName
                      )}
                      data-cy="modal_content"
                      ref={contentRef}
                      style={{ width: isMobile ? "100%" : width || "auto" }}
                    >
                      {children}
                    </div>
                  </FocusScope>
                </motion.div>
              </div>
            </RemoveScroll>
          </div>
        </Portal>
      )}
    </AnimatePresence>
  )
}

Modal.propTypes = {
  ariaLabel: PropTypes.string,
  ariaLabelledBy: PropTypes.string,
  children: PropTypes.node,
  contentClassName: PropTypes.string,
  contentIndex: PropTypes.number,
  disableClose: PropTypes.bool,
  headerClassName: PropTypes.string,
  headerHTML: PropTypes.node,
  headerVariant: PropTypes.oneOf(["fixed", "float", "inline"]),
  height: PropTypes.oneOfType([
    PropTypes.oneOf(["fit-content", "full"]),
    PropTypes.string,
  ]),
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  labelClassName: PropTypes.string,
  modalClassName: PropTypes.string,
  modalType: PropTypes.oneOf(["popup", "slideup", "slideright"]),
  noHeader: PropTypes.bool,
  onBack: PropTypes.func,
  onClose: PropTypes.func,
  onOutsideClick: PropTypes.func,
  shards: PropTypes.array,
  show: PropTypes.bool.isRequired,
  variant: PropTypes.oneOf(["base", "squircle"]),
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  withBackButton: PropTypes.bool,
}

Modal.defaultProps = {
  contentIndex: 0,
  headerVariant: "fixed",
  height: "100%",
  modalType: "slideup",
  noHeader: false,
  variant: "base",
  withBackButton: false,
}

Modal.displayName = "Modal"

export default Modal
